// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Platform surface ids and the boundary tag ──────────────────────────────
export const DATA_EXPLOITATION_REGISTER_SURFACES = {
    /** Slice A — one data activity and the route determination a person made for it. */
    activity: 'data_exploitation_activity',
    /** Slice B — a request as sent and the authority's result (grant or refusal). */
    request: 'data_exploitation_request',
    /** Slice C — one record per controlled-testing case on a procedure that lists this surface's template. */
    testing: 'data_exploitation_testing',
    /** Slice C — the production-reuse check, a sub-record of the testing record. */
    reuseCheck: 'data_exploitation_reuse_check',
    /** Slice D — an incident or violation notice and each notice sent. */
    notice: 'data_exploitation_notice',
    /** Slice E — the return, deletion, destruction or retention at the end of a grant. */
    disposition: 'data_exploitation_disposition',
    /** Slice F — incubator data enrichment and sharing. */
    incubator: 'data_exploitation_incubator',
    /** Slice H — a foreign-cooperation data-security risk-assessment record. */
    foreignAssessment: 'data_exploitation_foreign_assessment',
};
/** The one guidance tag every page of these registers renders through `BoundarySection`. */
export const DATA_EXPLOITATION_BOUNDARY_TAG = 'data_exploitation_boundary';
/** The collections the registers persist to. Platform names, one per record family. */
export const DATA_EXPLOITATION_ACTIVITIES_COLLECTION = 'grc_data_exploitation_activities';
export const DATA_EXPLOITATION_REQUESTS_COLLECTION = 'grc_data_exploitation_requests';
export const DATA_EXPLOITATION_TESTING_RECORDS_COLLECTION = 'grc_data_exploitation_testing_records';
export const DATA_EXPLOITATION_NOTICES_COLLECTION = 'grc_data_exploitation_notices';
export const DATA_EXPLOITATION_DISPOSITIONS_COLLECTION = 'grc_data_exploitation_dispositions';
export const DATA_EXPLOITATION_INCUBATOR_RECORDS_COLLECTION = 'grc_data_exploitation_incubator_records';
export const DATA_EXPLOITATION_FOREIGN_ASSESSMENTS_COLLECTION = 'grc_data_exploitation_foreign_assessments';
/** The same names, keyed by record family. */
export const DATA_EXPLOITATION_COLLECTIONS = {
    activities: DATA_EXPLOITATION_ACTIVITIES_COLLECTION,
    requests: DATA_EXPLOITATION_REQUESTS_COLLECTION,
    testingRecords: DATA_EXPLOITATION_TESTING_RECORDS_COLLECTION,
    notices: DATA_EXPLOITATION_NOTICES_COLLECTION,
    dispositions: DATA_EXPLOITATION_DISPOSITIONS_COLLECTION,
    incubatorRecords: DATA_EXPLOITATION_INCUBATOR_RECORDS_COLLECTION,
    foreignAssessments: DATA_EXPLOITATION_FOREIGN_ASSESSMENTS_COLLECTION,
};
/**
 * Pack attribute names the registers read. Platform vocabulary only: each is a
 * KEY a pack may author, never a value from any instrument. A flag the pack did
 * not author reads false.
 */
export const DATA_EXPLOITATION_PACK_ATTR = {
    /** Template-, section- or option-level: the requirement ids whose applicability decides whether it reaches the organisation. */
    appliesToRequirementIds: 'applies_to_requirement_ids',
    /** Option-level (a request's `outcome`): the outcome grants access — the request is a grant. */
    grantsAccess: 'grants_access',
    /** Option-level (a request's `outcome`): the outcome refuses, in whole or part, and needs the reasons. */
    requiresRefusalReasons: 'requires_refusal_reasons',
    /** Option-level (a request's `data_protection_marking`): the request is sent with its data-protection plan. */
    requiresProtectionPlan: 'requires_protection_plan',
    /** Option-level (a request's `resolution_category`): the installed deadline record whose period the authority runs. */
    deadlineId: 'deadline_id',
    /** Option-level (a disposition's `action`): a return, deletion or destruction recorded with its evidence. */
    requiresEvidence: 'requires_evidence',
    /** Option-level (a disposition's `action`): data kept under law, recorded with the provision it rests on. */
    requiresRetentionBasis: 'requires_retention_basis',
    /** Option-level (a reuse check's `authority_kind`): the kind is a separate authority whose reference is recorded. */
    requiresAuthorityReference: 'requires_authority_reference',
    /** Option-level (a request's `right_action_kind`): the decision withdraws the access a grant gave (pack 1.2.1). */
    withdrawsAccess: 'withdraws_access',
    /**
     * Option-level (a testing record's `mechanism_chosen`, pack 1.2.2): the
     * mechanism is the one whose term ceiling, single extension and extension
     * length the record applies. A mechanism without it follows its own law's
     * rules, which the record does not apply.
     */
    centreTermRules: 'centre_term_rules',
    /**
     * Option-level (a request's `right_action_kind`, pack 1.2.4): the decision
     * withdraws access for good (a revocation). Nothing is recorded after it; a
     * fresh right is a new request with its own result.
     */
    finalWithdrawal: 'final_withdrawal',
    /**
     * Option-level (a request's `right_action_kind`, pack 1.2.4): the
     * `right_action_kind` values whose withdrawal this decision ends. An ending
     * decision is accepted only directly after a withdrawal of a kind it names.
     */
    endsWithdrawalBy: 'ends_withdrawal_by',
};
/**
 * The `automation_status` of a deadline record that states a MAXIMUM (a term
 * ceiling), never a due date. Read, never a literal number.
 */
export const DATA_EXPLOITATION_STATED_MAXIMUM_STATUS = 'stated_maximum';
/** The register surfaces an export is taken of (`GET /export/:surfaceKey`). The reuse checks print inside the testing export. */
export const DATA_EXPLOITATION_EXPORT_SURFACE_KEYS = [
    'activity',
    'request',
    'testing',
    'notice',
    'disposition',
    'incubator',
    'foreignAssessment',
];
/** The locales an export is written in: the pack's own EN and VI. */
export const DATA_EXPLOITATION_EXPORT_LOCALES = ['en', 'vi'];
/** The most records one export carries (422 `DATA_EXPLOITATION_EXPORT_TOO_LARGE` above it). */
export const DATA_EXPLOITATION_EXPORT_MAX_RECORDS = 5000;
/** A request's state: platform vocabulary. `sent` is a person's statement that the organisation sent it. */
export const DATA_EXPLOITATION_REQUEST_STATES = ['draft', 'sent'];
/** The two certificates a testing record holds from the authority's copy. Platform vocabulary. */
export const DATA_EXPLOITATION_CERTIFICATE_KINDS = ['participation', 'completion'];
/** The only copy source a certificate may be recorded from: the platform issues none. */
export const DATA_EXPLOITATION_CERTIFICATE_COPY_SOURCE = 'authority_copy';
/**
 * How one requirement reads on the organisation's stated routes (D9). Never
 * *met* or *not applicable*: these say where a duty stands on the statement,
 * not whether anything was done.
 */
export const DATA_EXPLOITATION_REQUIREMENT_PRESENTATIONS = [
    /** A conditional requirement whose condition holds on the stated routes. */
    'applies_on_stated_routes',
    /**
     * A requirement the pack declares applicable with no route condition: it does
     * not turn on the stated routes. Never a duty from installing the pack (#663);
     * the member keeps its name.
     */
    'applies_to_every_installing_organisation',
    /** The routes are not stated, or stated as not known: a person's determination is awaited. */
    'depends_on_route_determination',
    /** A conditional requirement whose condition does not hold on the stated routes (a stated answer). */
    'does_not_apply_on_stated_routes',
    /** The pack declares the requirement applies to no installing organisation. */
    'carried_for_reference',
    /** The pack states no applicability for the requirement. */
    'applicability_not_declared',
];
/**
 * The append-only sub-records of a testing record that may be marked recorded
 * in error, by their route segment
 * (`POST /testing-records/:id/<segment>/:subId/recorded-in-error`). The sub id
 * is the entry's own id: a risk-plan version's `version_number`, an extension's
 * `extension_id`, a Centre act's `action_id`, a certificate's `kind`, a reuse
 * check's `check_id`.
 */
export const DATA_EXPLOITATION_TESTING_CORRECTABLE_SUBRECORDS = [
    'risk-plan-versions',
    'extensions',
    'centre-actions',
    'certificates',
    'reuse-checks',
];
/** The longest reason a correction carries. */
export const DATA_EXPLOITATION_CORRECTION_REASON_MAX = 2000;
// ── Slice H — foreign-cooperation risk assessments ─────────────────────────
/** The four measure fields a person records during a cooperation. The record's own field ids. */
export const DATA_EXPLOITATION_MEASURE_FIELDS = [
    'management_measures',
    'supervision_measures',
    'tracing_measures',
    'access_control_measures',
];
// ── Error codes ────────────────────────────────────────────────────────────
/**
 * Remediation X (G1-3, pack 1.2.4) — `details.reason` on a 422
 * `DATA_EXPLOITATION_RIGHT_ACTION_NOT_ALLOWED`. Each is read from the option
 * attributes, never from an option id.
 */
export const DATA_EXPLOITATION_RIGHT_ACTION_REASONS = {
    /** An ending decision while no withdrawal of a kind it names is in force on its date. */
    endsNoWithdrawalItNames: 'ends_no_withdrawal_it_names',
    /**
     * An action dated while a final withdrawal (a revocation) stands whose option
     * does not name its kind in `ends_withdrawal_by`.
     */
    afterFinalWithdrawal: 'after_final_withdrawal',
    /** A final withdrawal dated before an action already recorded. */
    finalBeforeLaterAction: 'final_before_later_action',
    /**
     * Remediation Y (P3-1): an action that, inserted at its date, would leave an
     * action already recorded after it without its basis (an ending that then
     * ends nothing, an action after a revocation). `details.later_action_id`.
     */
    laterActionNotAllowed: 'later_action_not_allowed',
    /**
     * Remediation Z (gate pass 3, G3-2): an action that, inserted at its date,
     * would end the decision that an ending already recorded on this grant ends.
     * Every ending's target is fixed when it is recorded, so an insertion never
     * moves it. `details.later_action_id` (that ending) and
     * `details.ends_action_id` (the decision both would end).
     */
    wouldChangeEndingTarget: 'would_change_ending_target',
    /**
     * Remediation Z (G3-2): a correction (recorded in error) refused because an
     * ending in effect on this grant rests on the entry — it is the decision
     * that ending ends, or the ending would otherwise be left without its basis.
     * Correct the ending first. `details.ending_action_id`.
     */
    endingRestsOnIt: 'ending_rests_on_it',
};
/** `details.reason` on a 422 `DATA_EXPLOITATION_DATE_ORDER_INVALID`. Every refusal names one. */
export const DATA_EXPLOITATION_DATE_ORDER_REASONS = {
    termEndBeforeStart: 'term_end_before_start',
    extensionNotAfterTerm: 'extension_not_after_term',
    noticeBeforeDiscovery: 'notice_before_discovery',
    purposeEndBeforeSent: 'purpose_end_before_sent',
    decisionBeforeSent: 'decision_before_sent',
    /** A suspension or revocation dated before the grant's decision. */
    rightActionBeforeDecision: 'right_action_before_decision',
    // ── Remediation X (G1-11) ──
    /** A completion certificate dated before the participation certificate (Điều 27 khoản 6 order). */
    completionBeforeParticipation: 'completion_before_participation',
    /** An extension decided before the participation certificate, both recorded. */
    extensionDecisionBeforeParticipation: 'extension_decision_before_participation',
    /** A grant's approved term ending before the grant was decided. */
    approvedTermEndBeforeDecision: 'approved_term_end_before_decision',
    /** A grant's purpose ending before the grant was decided. */
    purposeEndBeforeDecision: 'purpose_end_before_decision',
    /** A disposition done before the end it disposes of. */
    dispositionBeforeEnd: 'disposition_before_end',
    /** A disposition's end dated before the grant it answers was decided. */
    endBeforeGrantDecision: 'end_before_grant_decision',
    /** An incubator listing dated before the completion certificate, both recorded. */
    listingBeforeCompletionCertificate: 'listing_before_completion_certificate',
    /**
     * Remediation Y (P3-0): a past act — sent, decided, issued, done, checked,
     * assessed, listed, shared, deployed, discovered — dated after today in the
     * organisation's zone. Planned or approved dates (an approved term's end, an
     * extension's end, a term's start) are not refused.
     */
    datedInFuture: 'dated_in_future',
};
export const DATA_EXPLOITATION_ERROR_CODES = {
    recordNotFound: 'DATA_EXPLOITATION_RECORD_NOT_FOUND',
    surfaceUnknown: 'DATA_EXPLOITATION_SURFACE_UNKNOWN',
    noDeclaringFramework: 'DATA_EXPLOITATION_REGISTER_NOT_DECLARED',
    optionNotDeclared: 'DATA_EXPLOITATION_OPTION_NOT_DECLARED',
    optionNotOffered: 'DATA_EXPLOITATION_OPTION_NOT_OFFERED',
    sectionNotOffered: 'DATA_EXPLOITATION_SECTION_NOT_OFFERED',
    /** A route value outside the organisation-profile vocabulary, so it can never be compared with the statement. */
    routeNotInProfileVocabulary: 'DATA_EXPLOITATION_ROUTE_NOT_IN_PROFILE_VOCABULARY',
    /** 422: an activity records routes and `no_route` together, or neither. */
    routeDeterminationInvalid: 'DATA_EXPLOITATION_ROUTE_DETERMINATION_INVALID',
    versionConflict: 'DATA_EXPLOITATION_RECORD_VERSION_CONFLICT',
    userNotFound: 'DATA_EXPLOITATION_USER_NOT_FOUND',
    attachmentNotOwned: 'DATA_EXPLOITATION_ATTACHMENT_NOT_OWNED',
    dateOrderInvalid: 'DATA_EXPLOITATION_DATE_ORDER_INVALID',
    /** 422: a request saved as sent without the contents the template requires. */
    requestContentsRequired: 'DATA_EXPLOITATION_REQUEST_CONTENTS_REQUIRED',
    /** 422: a result recorded on a request that is not sent. */
    resultBeforeSent: 'DATA_EXPLOITATION_RESULT_BEFORE_SENT',
    /** 422: a grant or refusal without its decision reference and deciding body (and the fields of its kind). */
    decisionFieldsRequired: 'DATA_EXPLOITATION_DECISION_FIELDS_REQUIRED',
    /** 422: a field of one outcome kind recorded on another (grant fields on a refusal, reasons on a grant). */
    fieldsNotForOutcome: 'DATA_EXPLOITATION_FIELDS_NOT_FOR_OUTCOME',
    /**
     * 422: a request recorded as sent moved back to draft (Remediation X, G1-12:
     * refused whether or not a result is recorded — `sent` is a person's
     * statement of a past act; a mistaken date is corrected on `sent_at`).
     */
    requestStateChangeNotAllowed: 'DATA_EXPLOITATION_REQUEST_STATE_CHANGE_NOT_ALLOWED',
    /** 422: a grant moved away from a grant while records rest on it. */
    grantHasRestingRecords: 'DATA_EXPLOITATION_GRANT_HAS_RESTING_RECORDS',
    /** 404: a linked grant is not a request of this organisation. */
    grantNotFound: 'DATA_EXPLOITATION_GRANT_NOT_FOUND',
    /** 422: a linked request is not a recorded grant. */
    notAGrant: 'DATA_EXPLOITATION_NOT_A_GRANT',
    /** 409: the end of a grant's purpose is already recorded. */
    purposeEndAlreadyRecorded: 'DATA_EXPLOITATION_PURPOSE_END_ALREADY_RECORDED',
    /** 404: a linked controlled-testing case, incident, deletion run, vendor or transfer dossier is not this organisation's. */
    linkedRecordNotFound: 'DATA_EXPLOITATION_LINKED_RECORD_NOT_FOUND',
    /** 422: the case's procedure lists no template of the testing surface. */
    caseNotEligible: 'DATA_EXPLOITATION_CASE_NOT_ELIGIBLE',
    /** 409: a testing record is already open on the case. */
    testingRecordExists: 'DATA_EXPLOITATION_TESTING_RECORD_EXISTS',
    /** 422: an extension needs the approved term recorded first. */
    approvedTermNotRecorded: 'DATA_EXPLOITATION_APPROVED_TERM_NOT_RECORDED',
    /** 422: one extension only. */
    extensionAlreadyRecorded: 'DATA_EXPLOITATION_EXTENSION_ALREADY_RECORDED',
    /** 422: an extension longer than the approved period. `details.approved_period_days` and `details.extension_days`. */
    extensionTooLong: 'DATA_EXPLOITATION_EXTENSION_TOO_LONG',
    /** 409: the authority's copy of this certificate is already recorded. */
    certificateAlreadyRecorded: 'DATA_EXPLOITATION_CERTIFICATE_ALREADY_RECORDED',
    /** 422: the completion certificate before the participation certificate. */
    participationCertificateNotRecorded: 'DATA_EXPLOITATION_PARTICIPATION_CERTIFICATE_NOT_RECORDED',
    /** 422: a reuse check without the authority's reference, unless the kind holds none. */
    authorityReferenceRequired: 'DATA_EXPLOITATION_AUTHORITY_REFERENCE_REQUIRED',
    /** 422: a disposition without its evidence, or a retention without the provision of law. */
    dispositionFieldsRequired: 'DATA_EXPLOITATION_DISPOSITION_FIELDS_REQUIRED',
    /** 422: an export would carry more than `DATA_EXPLOITATION_EXPORT_MAX_RECORDS` records. */
    exportTooLarge: 'DATA_EXPLOITATION_EXPORT_TOO_LARGE',
    /** 422 (Remediation X, G1-3): a right action the recorded actions do not allow; `details.reason` from `DATA_EXPLOITATION_RIGHT_ACTION_REASONS`. */
    rightActionNotAllowed: 'DATA_EXPLOITATION_RIGHT_ACTION_NOT_ALLOWED',
    /** 404 (Remediation Y): the sub-record a correction names is not on this record. */
    subrecordNotFound: 'DATA_EXPLOITATION_SUBRECORD_NOT_FOUND',
    /** 409 (Remediation Y): the sub-record is already marked recorded in error; a correction is not undone or repeated. */
    alreadyRecordedInError: 'DATA_EXPLOITATION_ALREADY_RECORDED_IN_ERROR',
    /**
     * 409 (Remediation Z, G3-5): the participation certificate is not corrected
     * while a completion certificate in effect rests on it — correct the
     * completion certificate first. See `certificate_correctable`.
     */
    completionRestsOnParticipation: 'DATA_EXPLOITATION_COMPLETION_RESTS_ON_PARTICIPATION',
    /**
     * 409 (Remediation Z, G3-6): an end-of-purpose mark is not corrected while a
     * disposition of the grant states that end (its `ended_on` is the mark's
     * date) — edit the disposition's end first. `details.disposition_ids`,
     * `details.ended_on`.
     */
    dispositionRestsOnPurposeEnd: 'DATA_EXPLOITATION_DISPOSITION_RESTS_ON_PURPOSE_END',
};
//# sourceMappingURL=data-exploitation.js.map